import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuthToken, clearAuthToken } from '@/lib/authToken';
import { isTokenExpired } from '@/lib/api';

interface RequireAuthProps {
  children: (token: string) => React.ReactNode;
}

const RequireAuth = ({ children }: RequireAuthProps) => {
  const [isValidating, setIsValidating] = useState(true);
  const [validToken, setValidToken] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.log('No token found in RequireAuth');
          setIsValidating(false);
          return;
        }

        console.log('Validating token in RequireAuth...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('Token is expired in RequireAuth, clearing auth data');
          // Clear all auth data
          clearAuthToken();
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('username');
          setValidToken(null);
        } else {
          console.log('Token is valid in RequireAuth');
          const authToken = getAuthToken();
          setValidToken(authToken);
        }
      } catch (error) {
        console.error('Error validating token in RequireAuth:', error);
        // Clear auth data on error
        clearAuthToken();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        setValidToken(null);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, []);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating session...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!validToken) {
    return <Navigate to="/" replace />;
  }

  return <>{children(validToken)}</>;
};

export default RequireAuth; 